import { Avatar, Box, Button, Select, Modal } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconPlus, IconUser } from '@tabler/icons-react'
import { useState } from 'react'
import { useUsersInsert } from '../../api/hooks/Users/useUsersInsert'
import GuardianInfoModal from './GuardianInfoModal'
import SaveButton from '../common/SaveButton'
import ModalInput from '../common/ModalInput'
import styles from '../../styles/manager/AddUserModal.module.scss'
import { useLocation } from 'react-router-dom'
import { Icon } from '../../assets/icons'
import { formatPhoneNumber } from '../../utils/utils'
import { modals } from '@mantine/modals'
import BaseButton from '../common/BaseButton'
import { apiRequest } from '../../utils/api'
import { useAuthStore } from '../../store'

const AddUserModal = ({ opened, onClose }) => {
  const [guardianModalOpened, setGuardianModalOpened] = useState(false)
  const user = useAuthStore((state) => state.user)
  const centerId = user?.CENTER_ID
  const [parentId, setParentId] = useState(null)
  const [guardianList, setGuardianList] = useState([])
  const [idAvailable, setIdAvailable] = useState(null)
  const [formData, setFormData] = useState({
    NAME: '',
    EMAIL: '',
    BIRTH: '',
    PHONE: '',
    SCHOOL: '',
    ADDR: '',
    USER_ID: '',
    PASSWORD: '',
    TEST: 0,
    LEVEL: 0,
    CENTER_ID: centerId,
    STATE: 'Y',
    TYPE: 'U', // 기본: Student(U). T=Teacher, F=Offline
    COUNTRY: '',
    CITY: '',
    POSTCODE: '',
  })

  const { mutate: insertUser } = useUsersInsert()

  const countryOptions = [
    { value: 'KR', label: 'South Korea' },
    { value: 'US', label: 'United States' },
    { value: 'JP', label: 'Japan' },
    { value: 'CN', label: 'China' },
    { value: 'VN', label: 'Vietnam' },
    { value: 'GB', label: 'United Kingdom' },
    { value: 'FR', label: 'France' },
    { value: 'DE', label: 'Germany' },
    { value: 'CA', label: 'Canada' },
    { value: 'AU', label: 'Australia' },
    { value: 'IN', label: 'India' },
    { value: 'TH', label: 'Thailand' },
    { value: 'ID', label: 'Indonesia' },
    { value: 'PH', label: 'Philippines' },
    { value: 'RU', label: 'Russia' },
    { value: 'IT', label: 'Italy' },
    { value: 'ES', label: 'Spain' },
    { value: 'MY', label: 'Malaysia' },
    { value: 'SG', label: 'Singapore' },
    { value: 'BR', label: 'Brazil' },
    { value: 'MX', label: 'Mexico' },
    { value: 'SA', label: 'Saudi Arabia' },
    { value: 'TR', label: 'Turkey' },
    { value: 'AR', label: 'Argentina' },
    { value: 'ZA', label: 'South Africa' },
  ]

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleAddGuardian = (guardian) => {
    setGuardianList((prev) => [...prev, guardian])
    setParentId(guardian.id)
    setGuardianModalOpened(false)
  }

  const handleRemoveGuardian = (guardianId) => {
    setGuardianList((prev) => prev.filter((g) => g.id !== guardianId))
  }

  const handleSaveButton = () => {
    if (
      !formData.NAME ||
      !formData.USER_ID ||
      !formData.PASSWORD ||
      !formData.POSTCODE
    ) {
      notifications.show({
        title: 'Error',
        message:
          'Please fill in required fields (Name, PostCode, ID, Password)',
        color: 'red',
      })
      return
    }

    if (!isValidPassword(formData.PASSWORD)) {
      notifications.show({
        title: 'Invalid password',
        message:
          'At least 8 characters with letters and numbers/special characters',
        color: 'red',
      })
      return
    }

    // API 데이터 변환
    const apiData = {
      NAME: formData.NAME,
      EMAIL: formData.EMAIL,
      BIRTH: formData.BIRTH,
      PHONE: formData.PHONE,
      SCHOOL: formData.SCHOOL,
      ADDR: formData.ADDR,
      USER_ID: formData.USER_ID,
      PASSWORD: formData.PASSWORD,
      TEST: Number(formData.TEST) || 0,
      LEVEL: Number(formData.LEVEL) || 0,
      CENTER_ID: centerId,
      PARENT_ID: parentId,
      STATE: formData.STATE,
      TYPE: formData.TYPE,
      COUNTRY: formData.COUNTRY,
      CITY: formData.CITY,
      POSTCODE: formData.POSTCODE,
      SUB: 'Y'
    }

    insertUser(apiData, {
      onSuccess: () => {
        notifications.show({
          title: 'Success',
          message: 'User created successfully',
          color: 'green',
        })
        handleClose()
      },
      onError: (error) => {
        const status = error?.response?.status || error?.status || null
        if (status === 409) {
          notifications.show({
            title: 'Error',
            message: 'This ID is already in use',
            color: 'red',
          })
        } else {
          notifications.show({
            title: 'Error',
            message: 'Failed to create user. Please try again.',
            color: 'red',
          })
        }
        console.error('User creation failed:', error)
      },
    })
  }

  const showAlertModal = (message) => {
    modals.open({
      zIndex: 1002,
      children: (
        <div className="alert-container">
          <img
            src="/assets/rabbit-blue-monster-cry.png"
            alt="avatar"
            className="rabbit-monster"
          />
          <p className="alert-text">{message}</p>
          <BaseButton
            onClick={() => modals.closeAll()}
            className="alert-button"
          >
            Confirm
          </BaseButton>
        </div>
      ),
      centered: true,
      withCloseButton: false,
    })
  }

  const checkIdDuplicate = async () => {
    const userId = formData.USER_ID?.trim() || ''

    if (!userId) {
      showAlertModal('Enter your ID')
      setIdAvailable(null)
      return
    }

    try {
      const res = await apiRequest('/checkId', 'POST', { ID: userId })
      setIdAvailable(res.available)
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: 'ID unavailable',
        color: 'red',
      })
      setIdAvailable(null)
    }
  }

  const isValidPassword = (pw) => {
    const lengthCheck = pw.length >= 8
    const letterCheck = /[a-zA-Z]/.test(pw)
    const numberOrSymbolCheck = /[\d\W]/.test(pw)

    return lengthCheck && letterCheck && numberOrSymbolCheck
  }

  const handleClose = () => {
    setFormData({
      NAME: '',
      EMAIL: '',
      BIRTH: '',
      PHONE: '',
      SCHOOL: '',
      ADDR: '',
      USER_ID: '',
      PASSWORD: '',
      TEST: 0,
      LEVEL: 0,
      CENTER_ID: centerId,
      STATE: 'Y',
      COUNTRY: '',
      CITY: '',
      POSTCODE: '',
    })
    setGuardianList([])
    onClose()
  }

  return (
    <>
      <Modal
        opened={opened}
        onClose={handleClose}
        size="xl"
        padding="xl"
        styles={{
          content: {
            borderRadius: '15px',
          },
          close: {
            top: '-20px',
            right: '10px',
          },
        }}
      >
        <p className={styles.title}>Add User</p>

        <div className={styles.container}>
          <div className={styles.topWrapper}>
            <div className={styles.userInfoWrapper}>
              <div className={styles.userInfoInnerWrapper}>
                <Avatar size={50} color="gray" className={styles.avatar}>
                  <IconUser size={40} />
                </Avatar>
                <div className={styles.userFormWrapper}>
                  <p className={styles.textLg}>User name</p>
                  <ModalInput
                    placeholder="Insert name"
                    value={formData.NAME}
                    onChange={(e) => handleInputChange('NAME', e.target.value)}
                    required
                  />
                  <p className={`${styles.textLg} ${styles.emailLabel}`}>
                    email address
                  </p>
                  <ModalInput
                    placeholder="Insert email address"
                    value={formData.EMAIL}
                    onChange={(e) => handleInputChange('EMAIL', e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className={styles.levelInfoWrapper}>
              <div className={styles.levelHeaderWrapper}>
                <Icon name="info" size={20} />
                <p className={styles.textLg}>Level info</p>
              </div>
              <div className={styles.levelInputsWrapper}>
                <div className={styles.levelInputItem}>
                  <p className={`${styles.textSm} ${styles.inputLabel}`}>
                    Level
                  </p>
                  <ModalInput
                    placeholder="0"
                    type="number"
                    value={formData.LEVEL}
                    onChange={(e) => handleInputChange('LEVEL', e.target.value)}
                  />
                </div>
                <div className={styles.levelInputItem}>
                  <p className={`${styles.textSm} ${styles.inputLabel}`}>
                    Status
                  </p>
                  <Select
                    data={[
                      { value: 'T', label: 'Teacher' },
                      { value: 'U', label: 'Student' },
                      { value: 'F', label: 'Offline' },
                    ]}
                   placeholder="Select Status"
                   value={formData.TYPE}
                   onChange={(value) => handleInputChange('TYPE', value || 'U')}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className={styles.generalInfoWrapper}>
            <div className={styles.generalInfoHeaderWrapper}>
              <Icon name="info" size={20} />
              <p className={styles.textLg}>General information</p>
            </div>

            <div className={styles.generalInfoFieldsWrapper}>
              <div className={styles.fieldItem}>
                <p className={`${styles.textSm} ${styles.inputLabel}`}>ID</p>
                <div
                  style={{ display: 'flex', gap: '10px', maxWidth: '200px' }}
                >
                  <ModalInput
                    value={formData.USER_ID}
                    onChange={(e) =>
                      handleInputChange('USER_ID', e.target.value)
                    }
                    required
                  />
                  <div>
                    <SaveButton
                      style={{ padding: '9px 4px' }}
                      onClick={checkIdDuplicate}
                    >
                      Confirm
                    </SaveButton>
                  </div>
                </div>
              </div>
              {idAvailable === false && (
                <p
                  style={{
                    color: 'red',
                    position: 'relative',
                    left: '-180px',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <img src="/assets/icons/wrong.png" alt="error" style={{ width: 14, height: 14, objectFit: 'contain', verticalAlign: 'middle', marginRight: 4 }} />This ID is already in use
                </p>
              )}
              {idAvailable === true && (
                <p
                  style={{
                    color: 'green',
                    position: 'relative',
                    left: '-190px',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <img src="/assets/icons/check.png" alt="ok" style={{ width: 14, height: 14, objectFit: 'contain', verticalAlign: 'middle', marginRight: 4 }} />This ID can be used
                </p>
              )}

              <div className={styles.fieldItem}>
                <p className={`${styles.textSm} ${styles.inputLabel}`}>
                  Password
                </p>
                <ModalInput
                  type="password"
                  value={formData.PASSWORD}
                  onChange={(e) =>
                    handleInputChange('PASSWORD', e.target.value)
                  }
                  required
                />
              </div>

              <div className={styles.fieldItem}>
                <p className={`${styles.textSm} ${styles.inputLabel}`}>
                  Date of birth
                </p>
                <ModalInput
                  placeholder="yyyy-mm-dd"
                  type="date"
                  value={formData.BIRTH}
                  onChange={(e) => handleInputChange('BIRTH', e.target.value)}
                  style={{ width: '200px' }}
                />
              </div>

              <div className={styles.fieldItem}>
                <p className={`${styles.textSm} ${styles.inputLabel}`}>
                  School
                </p>
                <ModalInput
                  value={formData.SCHOOL}
                  onChange={(e) => handleInputChange('SCHOOL', e.target.value)}
                />
              </div>
              <div className={styles.fieldItem}>
                <p className={`${styles.textSm} ${styles.inputLabel}`}>Phone</p>
                <ModalInput
                  value={formData.PHONE}
                  onChange={(e) => {
                    const formatted = formatPhoneNumber(e.target.value)
                    handleInputChange('PHONE', formatted)
                  }}
                  placeholder="000-0000-0000"
                />
              </div>

              <div className={styles.divider}></div>

              <div className={styles.fieldItem}>
                <p className={`${styles.textSm} ${styles.inputLabel}`}>
                  Country
                </p>
                <Select
                  data={countryOptions}
                  value={formData.COUNTRY}
                  onChange={(value) =>
                    handleInputChange('COUNTRY', value || '')
                  }
                  placeholder="Select country"
                />
              </div>

              <div className={styles.fieldItem}>
                <p className={`${styles.textSm} ${styles.inputLabel}`}>City</p>
                <ModalInput
                  value={formData.CITY}
                  onChange={(e) => handleInputChange('CITY', e.target.value)}
                  placeholder="City name"
                />
              </div>
              <div className={styles.fieldItem}>
                <p className={`${styles.textSm} ${styles.inputLabel}`}>
                  Postcode
                </p>
                <ModalInput
                  value={formData.POSTCODE}
                  onChange={(e) =>
                    handleInputChange('POSTCODE', e.target.value)
                  }
                  placeholder="Zip or postal code"
                  required
                />
              </div>
              <div className={styles.fullWidthField}>
                <p className={`${styles.textSm} ${styles.inputLabel}`}>
                  Address
                </p>
                <ModalInput
                  value={formData.ADDR}
                  onChange={(e) => handleInputChange('ADDR', e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className={styles.bottomWrapper}>
            <div className={styles.guardianWrapper}>
              <div className={styles.guardianHeaderWrapper}>
                <Icon name="info" size={20} />
                <p className={styles.textLg}>Guardian info</p>
              </div>

              {guardianList.length > 0 && (
                <div className={styles.guardianListWrapper}>
                  {guardianList.map((guardian) => (
                    <div key={guardian.id} className={styles.guardianItem}>
                      <div className={styles.guardianItemInfo}>
                        <Avatar size={30} color="blue">
                          <IconUser size={16} />
                        </Avatar>
                        <div>
                          <p className={styles.textSm}>{guardian.name}</p>
                          <p
                            className={`${styles.textXs} ${styles.textDimmed}`}
                          >
                            {guardian.email}
                          </p>
                        </div>
                      </div>
                      <Button
                        size="xs"
                        variant="subtle"
                        color="red"
                        onClick={() => handleRemoveGuardian(guardian.id)}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              <Box
                className={styles.addGuardianBox}
                onClick={() => setGuardianModalOpened(true)}
              >
                <div className={styles.addGuardianContent}>
                  <IconPlus size={24} color="#6c757d" />
                  <p className={styles.textLg} style={{ color: '#6c757d' }}>
                    Add Guardian
                  </p>
                </div>
              </Box>
            </div>

            <div className={styles.statusWrapper}>
              <div className={styles.statusInnerWrapper}>
                <div className={styles.statusHeaderWrapper}>
                  <Icon name="info" size={20} />
                  <p className={styles.textLg}>Status</p>
                </div>
                <div className={styles.statusButtonsWrapper}>
                  <Button
                    variant={formData.STATE === 'Y' ? 'filled' : 'outline'}
                    color={formData.STATE === 'Y' ? 'gray' : 'gray'}
                    onClick={() => handleInputChange('STATE', 'Y')}
                    className={
                      formData.STATE === 'Y'
                        ? styles.activeButton
                        : styles.activeButtonOutline
                    }
                  >
                    Active
                  </Button>
                  <Button
                    variant={formData.STATE === 'N' ? 'filled' : 'outline'}
                    color="gray"
                    onClick={() => handleInputChange('STATE', 'N')}
                    className={
                      formData.STATE === 'N'
                        ? styles.inactiveButton
                        : styles.inactiveButtonOutline
                    }
                  >
                    Inactive
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.saveButtonWrapper}>
            <SaveButton onClick={handleSaveButton}>Save</SaveButton>
          </div>
        </div>
      </Modal>

      <GuardianInfoModal
        zIndex={999}
        opened={guardianModalOpened}
        onClose={() => setGuardianModalOpened(false)}
        onSave={handleAddGuardian}
        studentInfo={{
          name: formData.NAME,
          email: formData.EMAIL,
        }}
      />
    </>
  )
}

export default AddUserModal
